import * as THREE from 'three';
import { FikoGoochShader } from '../shader/FikoGoochShader';
import { FikoToonShader } from '../shader/FikoToonShader';
import { FikoHatchingShader } from '../shader/FikoHatchingShader';
import { FikoSilhouetteShader } from '../shader/FikoSilhouetteShader';

export type NPRMod = 'pbr' | 'gooch' | 'toon' | 'hatching' | 'silhouette';

export class FikoNPRRenderer {
  private static instance: FikoNPRRenderer;
  private aktifMod: NPRMod = 'pbr';
  private orijinalMalzemeler: Map<THREE.Mesh, THREE.Material | THREE.Material[]>;
  private goochShader: FikoGoochShader;
  private toonShader: FikoToonShader;
  private hatchingShader: FikoHatchingShader;
  private silhouetteShader: FikoSilhouetteShader;
  private lightDirection: THREE.Vector3 = new THREE.Vector3(0, 0, 1).normalize(); // Varsayılan ışık yönü

  private constructor() {
    this.orijinalMalzemeler = new Map();
    this.goochShader = new FikoGoochShader();
    this.toonShader = new FikoToonShader();
    this.hatchingShader = new FikoHatchingShader();
    this.silhouetteShader = new FikoSilhouetteShader();

    // Gooch, Toon, Hatching shader'larına ışık yönünü güncelle
    this.goochShader.uniforms.uLightDir.value = this.lightDirection;
    this.toonShader.uniforms.uLightDirection.value = this.lightDirection;
    this.hatchingShader.uniforms.uLightDir.value = this.lightDirection;
  }

  static getInstance(): FikoNPRRenderer {
    if (!FikoNPRRenderer.instance) {
      FikoNPRRenderer.instance = new FikoNPRRenderer();
    }
    return FikoNPRRenderer.instance;
  }

  setLightDirection(direction: THREE.Vector3) {
    this.lightDirection.copy(direction).normalize();
    this.goochShader.uniforms.uLightDir.value = this.lightDirection;
    this.toonShader.uniforms.uLightDirection.value = this.lightDirection;
    this.hatchingShader.uniforms.uLightDir.value = this.lightDirection;
  }

  modDegistir(mod: NPRMod, sahne: THREE.Scene): void {
    if (this.aktifMod === mod) return; // Zaten aktif olan mod ise bir şey yapma

    // Mevcut moddan çıkarken orijinal malzemeleri geri yükle
    if (this.aktifMod !== 'pbr') {
      this.pbrGeriYukle(sahne);
    }

    this.aktifMod = mod;

    if (mod === 'pbr') {
      // PBR moduna geçildiğinde zaten geri yüklenmiş olacak
      return;
    }

    sahne.traverse((object) => {
      if (object instanceof THREE.Mesh) {
        if (!this.orijinalMalzemeler.has(object)) {
          this.orijinalMalzemeler.set(object, object.material);
        }

        let newMaterial: THREE.Material | THREE.Material[];

        switch (mod) {
          case 'gooch':
            newMaterial = this.goochShader;
            break;
          case 'toon':
            newMaterial = this.toonShader;
            break;
          case 'hatching':
            newMaterial = this.hatchingShader;
            break;
          case 'silhouette':
            // Silhouette modu için ana material ile birlikte silhouette shader
            const originalMaterial = this.orijinalMalzemeler.get(object);
            if (originalMaterial instanceof THREE.Material) {
              newMaterial = [originalMaterial, this.silhouetteShader];
            } else if (Array.isArray(originalMaterial)) {
              newMaterial = [...originalMaterial, this.silhouetteShader];
            } else {
              newMaterial = [new THREE.MeshStandardMaterial({ color: 0xcccccc }), this.silhouetteShader]; // Fallback
            }
            break;
          default:
            newMaterial = new THREE.MeshStandardMaterial({ color: 0xcccccc }); // Fallback
            break;
        }
        object.material = newMaterial;
      }
    });
  }

  pbrGeriYukle(sahne: THREE.Scene): void {
    sahne.traverse((object) => {
      if (object instanceof THREE.Mesh && this.orijinalMalzemeler.has(object)) {
        object.material = this.orijinalMalzemeler.get(object)!;
      }
    });
    this.orijinalMalzemeler.clear();
    this.aktifMod = 'pbr';
  }

  dispose(): void {
    this.goochShader.dispose();
    this.toonShader.dispose();
    this.hatchingShader.dispose();
    this.silhouetteShader.dispose();
    this.orijinalMalzemeler.clear(); // Map'i temizle
    // Singleton instance'ı sıfırla, böylece yeni bir instance oluşturulabilir
    (FikoNPRRenderer.instance as any) = null;
  }

  getAktifMod(): NPRMod {
    return this.aktifMod;
  }
}
